import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useSeason } from "../context/SeasonContext";
import { useSettings } from "../context/SettingsContext";

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function teamColor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return `hsl(${Math.abs(h) % 360}, 65%, 55%)`;
}

function nextSunday() {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? 0 : 7 - day));
  return d.toLocaleDateString("sv-SE"); // YYYY-MM-DD, local time
}

function fmtTime(t = "") {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return "—";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

function slotTime(raw = "") {
  return raw?.slice(0, 5) || "00:00";
}
function initials(name = "") {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

/** Canonical pair key — always smaller id first so order doesn't matter */
function pairKey(a, b) {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

// ─── Slot template: 8 slots across 3 time groups ─────────────────────────────
const SLOT_TEMPLATE = [
  { court: "Court 1", timeSlot: 1 },
  { court: "Court 2", timeSlot: 1 },
  { court: "Court 3", timeSlot: 1 },
  { court: "Court 1", timeSlot: 2 },
  { court: "Court 2", timeSlot: 2 },
  { court: "Court 3", timeSlot: 2 },
  { court: "Court 1", timeSlot: 3 },
  { court: "Court 2", timeSlot: 3 },
];

function buildSlots(settings, date, existingGames = []) {
  const times = {
    1: slotTime(settings?.game_time_slot_1),
    2: slotTime(settings?.game_time_slot_2),
    3: slotTime(settings?.game_time_slot_3),
  };
  return SLOT_TEMPLATE.map((tmpl, i) => {
    const match = existingGames.find(
      (g) =>
        g.game_date?.slice(0, 10) === date &&
        (g.court || "") === tmpl.court &&
        g.game_date?.slice(11, 16) === times[tmpl.timeSlot],
    );
    return {
      id: i,
      court: match?.court || tmpl.court,
      timeSlot: tmpl.timeSlot,
      date: match?.game_date?.slice(0, 10) || date,
      time: match?.game_date?.slice(11, 16) || times[tmpl.timeSlot],
      team1: match
        ? {
            id: match.team1.id,
            name: match.team1.name,
            logo_url: match.team1.logo_url,
          }
        : null,
      team2: match
        ? {
            id: match.team2.id,
            name: match.team2.name,
            logo_url: match.team2.logo_url,
          }
        : null,
      existingGameId: match?.id || null,
      originalTeam1Id: match?.team1?.id || null,
      originalTeam2Id: match?.team2?.id || null,
    };
  });
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ScheduleBuilder({ onBack }) {
  const { activeSeason } = useSeason();
  const { settings } = useSettings();

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [activeSlotId, setActiveSlotId] = useState(0);
  const [saving, setSaving] = useState(false);
  const [adminId, setAdminId] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);
  const [scheduleDate, setScheduleDate] = useState(() => nextSunday());
  const [successCount, setSuccessCount] = useState(null);

  // Conflict + history data
  const [playedPairs, setPlayedPairs] = useState(new Set()); // "minId-maxId"
  const [slotHistory, setSlotHistory] = useState({}); // teamId → { early, mid, late }

  // Preference note editing
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteValue, setNoteValue] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const noteRef = useRef(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setAdminId(data?.user?.id));
  }, []);

  // ── Load teams (including slot_preference_note) ───────────────────────────
  useEffect(() => {
    if (!activeSeason) return;
    setLoading(true);
    supabase
      .from("teams")
      .select("id, name, logo_url, slot_preference_note")
      .is("archived_at", null)
      .eq("season_id", activeSeason.id)
      .order("name")
      .then(({ data }) => {
        setTeams(data || []);
        setLoading(false);
      });
  }, [activeSeason]);

  // ── Load season-wide pair conflicts + slot history ────────────────────────
  const loadSeasonData = useCallback(async () => {
    if (!activeSeason || !settings) return;

    // All regular games this season (finished + scheduled) for conflict detection
    const { data: allGames } = await supabase
      .from("games")
      .select("id, team1_id, team2_id, game_date, court, status")
      .eq("season_id", activeSeason.id)
      .in("status", ["finished", "scheduled"])
      .eq("game_type", "regular");

    if (!allGames) return;

    // Build played pairs set
    const pairs = new Set();
    allGames.forEach((g) => pairs.add(pairKey(g.team1_id, g.team2_id)));
    setPlayedPairs(pairs);

    // Build slot history per team
    const t1 = slotTime(settings?.game_time_slot_1);
    const t2 = slotTime(settings?.game_time_slot_2);
    const t3 = slotTime(settings?.game_time_slot_3);

    const history = {};
    const addSlot = (teamId, gameTime) => {
      if (!history[teamId]) history[teamId] = { early: 0, mid: 0, late: 0 };
      const t = gameTime?.slice(11, 16);
      if (t === t1) history[teamId].early++;
      else if (t === t2) history[teamId].mid++;
      else if (t === t3) history[teamId].late++;
    };

    allGames.forEach((g) => {
      addSlot(g.team1_id, g.game_date);
      addSlot(g.team2_id, g.game_date);
    });
    setSlotHistory(history);
  }, [activeSeason, settings]);

  useEffect(() => {
    loadSeasonData();
  }, [loadSeasonData]);

  // ── Load slots for selected date ──────────────────────────────────────────
  const loadSlotsForDate = useCallback(
    async (date) => {
      if (!activeSeason || !settings) return;
      const { data: existing } = await supabase
        .from("games")
        .select(
          `id, game_date, court, status,
        team1:teams!games_team1_id_fkey(id, name, logo_url),
        team2:teams!games_team2_id_fkey(id, name, logo_url)`,
        )
        .eq("season_id", activeSeason.id)
        .eq("status", "scheduled")
        .gte("game_date", `${date}T00:00:00`)
        .lte("game_date", `${date}T23:59:59`);
      setSlots(buildSlots(settings, date, existing || []));
      setActiveSlotId(0);
      setEditingSlot(null);
    },
    [activeSeason, settings],
  );

  useEffect(() => {
    if (settings && activeSeason) loadSlotsForDate(scheduleDate);
  }, [settings, activeSeason, scheduleDate, loadSlotsForDate]);

  // ── Slot helpers ──────────────────────────────────────────────────────────
  const updateSlot = useCallback((slotId, patch) => {
    setSlots((prev) =>
      prev.map((s) => (s.id === slotId ? { ...s, ...patch } : s)),
    );
  }, []);

  const clearSlot = useCallback((slotId) => {
    setSlots((prev) =>
      prev.map((s) =>
        s.id === slotId ? { ...s, team1: null, team2: null } : s,
      ),
    );
  }, []);

  // ── Team click ────────────────────────────────────────────────────────────
  const handleTeamClick = useCallback(
    (team) => {
      setSlots((prev) => {
        const slot = prev.find((s) => s.id === activeSlotId);
        if (!slot) return prev;

        // If team already in this slot — remove it
        if (slot.team1?.id === team.id)
          return prev.map((s) =>
            s.id === activeSlotId
              ? { ...s, team1: slot.team2, team2: null }
              : s,
          );
        if (slot.team2?.id === team.id)
          return prev.map((s) =>
            s.id === activeSlotId ? { ...s, team2: null } : s,
          );

        // Conflict check — blocked if they've already played each other
        // (done in render to show visual; here we just silently skip)
        if (slot.team1 && playedPairs.has(pairKey(slot.team1.id, team.id)))
          return prev;

        if (!slot.team1)
          return prev.map((s) =>
            s.id === activeSlotId ? { ...s, team1: team } : s,
          );
        if (!slot.team2) {
          const updated = prev.map((s) =>
            s.id === activeSlotId ? { ...s, team2: team } : s,
          );
          const nextEmpty = updated.find(
            (s) => s.id !== activeSlotId && (!s.team1 || !s.team2),
          );
          if (nextEmpty) setTimeout(() => setActiveSlotId(nextEmpty.id), 180);
          return updated;
        }
        // Slot full — replace team1, shift old team1 → team2
        // But check new pair wouldn't conflict first
        if (playedPairs.has(pairKey(team.id, slot.team2.id))) return prev;
        return prev.map((s) =>
          s.id === activeSlotId ? { ...s, team1: team, team2: slot.team1 } : s,
        );
      });
    },
    [activeSlotId, playedPairs],
  );

  // ── Save ──────────────────────────────────────────────────────────────────
  const saveSchedule = async () => {
    const toSave = slots.filter((s) => {
      if (!s.team1 || !s.team2) return false;
      return !(
        s.existingGameId &&
        s.team1.id === s.originalTeam1Id &&
        s.team2.id === s.originalTeam2Id
      );
    });
    if (toSave.length === 0) {
      alert("No changes to save.");
      return;
    }
    setSaving(true);
    let saved = 0;
    try {
      for (const slot of toSave) {
        const game_date = `${slot.date}T${slot.time}:00`;
        if (slot.existingGameId) {
          const { error } = await supabase.functions.invoke("admin-actions", {
            body: {
              action: "updateScheduledGame",
              adminId,
              payload: {
                gameId: slot.existingGameId,
                game_date,
                court: slot.court,
                team1_id: slot.team1.id,
                team2_id: slot.team2.id,
              },
            },
          });
          if (error) throw error;
        } else {
          const { error } = await supabase.functions.invoke("admin-actions", {
            body: {
              action: "createScheduledGame",
              adminId,
              payload: {
                seasonId: activeSeason.id,
                team1_id: slot.team1.id,
                team2_id: slot.team2.id,
                game_date,
                court: slot.court,
              },
            },
          });
          if (error) throw error;
        }
        saved++;
      }
      setSuccessCount(saved);
      setTimeout(() => setSuccessCount(null), 3000);
      await Promise.all([loadSlotsForDate(scheduleDate), loadSeasonData()]);
    } catch (err) {
      alert(`Error saving: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Preference note editing ───────────────────────────────────────────────
  const startEditNote = (team, e) => {
    e.stopPropagation();
    setEditingNoteId(team.id);
    setNoteValue(team.slot_preference_note || "");
    setTimeout(() => noteRef.current?.focus(), 50);
  };

  const saveNote = async (teamId) => {
    setSavingNote(true);
    try {
      const { error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "updateTeamSlotPreference",
          adminId,
          payload: { teamId, note: noteValue },
        },
      });
      if (error) throw error;
      // Update local state optimistically
      setTeams((prev) =>
        prev.map((t) =>
          t.id === teamId
            ? { ...t, slot_preference_note: noteValue.trim() || null }
            : t,
        ),
      );
      setEditingNoteId(null);
    } catch (err) {
      alert(`Error saving note: ${err.message}`);
    } finally {
      setSavingNote(false);
    }
  };

  // ── Derived state ─────────────────────────────────────────────────────────
  const pendingChanges = slots.filter((s) => {
    if (!s.team1 || !s.team2) return false;
    return !(
      s.existingGameId &&
      s.team1.id === s.originalTeam1Id &&
      s.team2.id === s.originalTeam2Id
    );
  }).length;

  const usedTeamIds = new Set(
    slots.flatMap((s) => [s.team1?.id, s.team2?.id].filter(Boolean)),
  );
  const activeSlotTeam1Id = slots.find((s) => s.id === activeSlotId)?.team1?.id;

  const slotGroups = [1, 2, 3].map((ts) => ({
    timeSlot: ts,
    label: ts === 1 ? "Early" : ts === 2 ? "Mid" : "Late",
    time: slots.find((s) => s.timeSlot === ts)?.time || "",
    slots: slots.filter((s) => s.timeSlot === ts),
  }));
  const existingCount = slots.filter((s) => s.existingGameId).length;

  if (loading || !slots.length) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        Loading schedule builder…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-white transition-colors">
      {/* ── Top bar ── */}
      <div className="sticky top-0 z-30 bg-white/90 dark:bg-gray-950/90 backdrop-blur border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="min-w-0">
            <h1 className="text-xl font-black tracking-tight leading-none text-gray-900 dark:text-white">
              Schedule Builder
            </h1>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
              {activeSeason?.name}
              {existingCount > 0 && (
                <span className="ml-2 text-sky-500 dark:text-sky-400">
                  · {existingCount} existing game
                  {existingCount !== 1 ? "s" : ""} loaded
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input
            type="date"
            value={scheduleDate}
            onChange={(e) => e.target.value && setScheduleDate(e.target.value)}
            className="text-sm border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2
              bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-orange-500 focus:outline-none transition"
          />
          {successCount !== null && (
            <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-400/10 px-3 py-1.5 rounded-full">
              ✓ {successCount} saved!
            </span>
          )}
          {pendingChanges > 0 && successCount === null && (
            <span className="text-xs font-semibold text-orange-500 dark:text-orange-400 bg-orange-50 dark:bg-orange-400/10 px-3 py-1.5 rounded-full hidden sm:block">
              {pendingChanges} unsaved
            </span>
          )}
          <button
            onClick={saveSchedule}
            disabled={saving || pendingChanges === 0}
            className="bg-orange-600 hover:bg-orange-500
              disabled:bg-gray-200 dark:disabled:bg-gray-800
              disabled:text-gray-400 dark:disabled:text-gray-600
              text-white font-bold px-5 py-2.5 rounded-xl text-sm transition active:scale-95"
          >
            {saving
              ? "Saving…"
              : `Save${pendingChanges > 0 ? ` ${pendingChanges}` : ""} Change${pendingChanges !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        {/* ── Team picker ── */}
        <section>
          <div className="flex items-baseline gap-3 mb-3">
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Teams
            </h2>
            <span className="text-xs text-gray-300 dark:text-gray-600">
              {activeSlotTeam1Id
                ? "— strikethrough = already played this team · click to assign"
                : "— click to assign to the active slot"}
            </span>
          </div>
          <div className="bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
            <div className="flex flex-wrap gap-2">
              {teams.map((team) => {
                const isInActiveSlot =
                  slots.find((s) => s.id === activeSlotId)?.team1?.id ===
                    team.id ||
                  slots.find((s) => s.id === activeSlotId)?.team2?.id ===
                    team.id;
                const isUsedElsewhere =
                  usedTeamIds.has(team.id) && !isInActiveSlot;
                // Conflict: team1 is picked, this team has already faced them
                const isConflict =
                  activeSlotTeam1Id &&
                  activeSlotTeam1Id !== team.id &&
                  playedPairs.has(pairKey(activeSlotTeam1Id, team.id));
                const hist = slotHistory[team.id] || {
                  early: 0,
                  mid: 0,
                  late: 0,
                };
                const isEditingNote = editingNoteId === team.id;

                return (
                  <div
                    key={team.id}
                    className={`relative flex flex-col items-center gap-1 p-2 rounded-xl transition-all duration-150 group
                      ${
                        isConflict
                          ? "opacity-40 cursor-not-allowed"
                          : isUsedElsewhere
                            ? "opacity-40 hover:opacity-60 cursor-pointer"
                            : isInActiveSlot
                              ? "ring-2 ring-orange-500 bg-orange-50 dark:bg-orange-500/10 scale-105 cursor-pointer"
                              : "hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                      }`}
                    onClick={() =>
                      !isConflict && !isEditingNote && handleTeamClick(team)
                    }
                    title={
                      isConflict ? `Already played this matchup` : team.name
                    }
                  >
                    {/* Logo */}
                    <div
                      className={`w-14 h-14 rounded-xl overflow-hidden flex items-center justify-center border transition relative
                        ${
                          isInActiveSlot
                            ? "border-orange-400"
                            : "border-gray-200 dark:border-gray-700 group-hover:border-gray-300 dark:group-hover:border-gray-500"
                        }`}
                      style={{
                        background: team.logo_url
                          ? "white"
                          : teamColor(team.name),
                      }}
                    >
                      {team.logo_url ? (
                        <img
                          src={team.logo_url}
                          alt={team.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <span className="text-white text-xs font-black leading-none">
                          {initials(team.name)}
                        </span>
                      )}
                      {/* Conflict strikethrough overlay */}
                      {isConflict && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-black/60 rounded-xl">
                          <div className="w-full h-0.5 bg-red-500 rotate-45 scale-x-125" />
                        </div>
                      )}
                    </div>

                    {/* Name — strikethrough if conflict */}
                    <span
                      className={`text-xs text-center max-w-[72px] leading-tight line-clamp-2 font-medium
                      ${
                        isConflict
                          ? "line-through text-red-400 dark:text-red-500"
                          : "text-gray-600 dark:text-gray-300"
                      }`}
                    >
                      {team.name}
                    </span>

                    {/* Slot history pills */}
                    <div className="flex gap-0.5 mt-0.5">
                      {[
                        {
                          label: "E",
                          count: hist.early,
                          color:
                            "bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400",
                        },
                        {
                          label: "M",
                          count: hist.mid,
                          color:
                            "bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400",
                        },
                        {
                          label: "L",
                          count: hist.late,
                          color:
                            "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400",
                        },
                      ].map(({ label, count, color }) => (
                        <span
                          key={label}
                          className={`text-[9px] font-bold px-1 py-0.5 rounded ${color}`}
                          title={`${label === "E" ? "Early" : label === "M" ? "Mid" : "Late"}: ${count} games`}
                        >
                          {label}
                          {count}
                        </span>
                      ))}
                    </div>

                    {/* Preference note */}
                    {isEditingNote ? (
                      <div
                        className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-50 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <textarea
                          ref={noteRef}
                          value={noteValue}
                          onChange={(e) => setNoteValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") setEditingNoteId(null);
                          }}
                          rows={3}
                          placeholder="e.g. Prefers early slot · Nov only"
                          className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-lg p-1.5 resize-none
                            bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-orange-500 focus:outline-none"
                        />
                        <div className="flex gap-1 mt-1.5">
                          <button
                            onClick={() => saveNote(team.id)}
                            disabled={savingNote}
                            className="flex-1 text-[10px] font-semibold bg-orange-600 hover:bg-orange-500 text-white rounded-lg py-1 transition disabled:opacity-50"
                          >
                            {savingNote ? "…" : "Save"}
                          </button>
                          <button
                            onClick={() => setEditingNoteId(null)}
                            className="text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-2 transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : team.slot_preference_note ? (
                      <div
                        className="max-w-[80px] text-[9px] text-center leading-tight text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-1 py-0.5 cursor-text"
                        onClick={(e) => startEditNote(team, e)}
                        title="Click to edit preference note"
                      >
                        {team.slot_preference_note}
                      </div>
                    ) : (
                      <button
                        onClick={(e) => startEditNote(team, e)}
                        className="text-[9px] text-gray-300 dark:text-gray-700 hover:text-gray-500 dark:hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Add slot preference note"
                      >
                        + note
                      </button>
                    )}

                    {/* In-active-slot dot */}
                    {isInActiveSlot && (
                      <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-orange-500 rounded-full border-2 border-white dark:border-gray-950" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Game slots ── */}
        <section>
          <div className="flex items-baseline gap-3 mb-3">
            <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Game Slots
            </h2>
            <span className="text-xs text-gray-300 dark:text-gray-600">
              — click a slot to activate it, then pick teams above
            </span>
          </div>

          <div className="space-y-5">
            {slotGroups.map((group) => (
              <div key={group.timeSlot}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-orange-500 dark:text-orange-400 font-black text-sm tabular-nums shrink-0">
                    {fmtTime(group.time + ":00")}
                  </span>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0
                    ${
                      group.timeSlot === 1
                        ? "bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400"
                        : group.timeSlot === 2
                          ? "bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400"
                          : "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400"
                    }`}
                  >
                    {group.label}
                  </span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {group.slots.map((slot) => {
                    const isActive = slot.id === activeSlotId;
                    const isFull = slot.team1 && slot.team2;
                    const isExisting = !!slot.existingGameId;
                    const isDirty =
                      isFull &&
                      !(
                        isExisting &&
                        slot.team1.id === slot.originalTeam1Id &&
                        slot.team2.id === slot.originalTeam2Id
                      );

                    return (
                      <div
                        key={slot.id}
                        onClick={() => {
                          if (editingSlot !== slot.id) setActiveSlotId(slot.id);
                        }}
                        className={`relative rounded-2xl border-2 cursor-pointer transition-all duration-200
                          ${
                            isActive
                              ? "border-orange-500 bg-orange-50 dark:bg-orange-500/5 shadow-lg shadow-orange-500/10"
                              : isFull
                                ? "border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-400 dark:hover:border-gray-600"
                                : "border-gray-200 dark:border-gray-800 bg-white/60 dark:bg-gray-900/40 hover:border-gray-300 dark:hover:border-gray-700"
                          }`}
                      >
                        {isActive && (
                          <span
                            className="absolute -inset-px rounded-2xl border-2 border-orange-400/40 animate-ping pointer-events-none"
                            style={{ animationDuration: "2s" }}
                          />
                        )}
                        {isDirty && (
                          <div className="absolute top-0 left-0 right-0 h-0.5 bg-orange-500 rounded-t-2xl" />
                        )}

                        <div className="p-3">
                          <div className="flex items-center justify-between mb-3 min-h-[24px]">
                            {editingSlot === slot.id ? (
                              <SlotEditor
                                slot={slot}
                                onUpdate={(patch) => updateSlot(slot.id, patch)}
                                onDone={() => setEditingSlot(null)}
                              />
                            ) : (
                              <>
                                <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                  <span className="text-xs font-bold text-gray-600 dark:text-gray-400">
                                    {slot.court}
                                  </span>
                                  <span className="text-gray-300 dark:text-gray-700 text-xs">
                                    ·
                                  </span>
                                  <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                                    {fmtTime(slot.time + ":00")}
                                  </span>
                                  <span className="text-gray-300 dark:text-gray-700 text-xs">
                                    ·
                                  </span>
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {slot.date}
                                  </span>
                                  {isExisting && !isDirty && (
                                    <span className="text-[10px] font-semibold text-sky-500 dark:text-sky-400 bg-sky-50 dark:bg-sky-400/10 px-1.5 py-0.5 rounded-full">
                                      saved
                                    </span>
                                  )}
                                  {isDirty && (
                                    <span className="text-[10px] font-semibold text-orange-500 bg-orange-50 dark:bg-orange-400/10 px-1.5 py-0.5 rounded-full">
                                      edited
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0 ml-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingSlot(slot.id);
                                    }}
                                    className="text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 text-xs px-1.5 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                                  >
                                    ✏️
                                  </button>
                                  {(slot.team1 || slot.team2) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        clearSlot(slot.id);
                                      }}
                                      className="text-gray-300 dark:text-gray-600 hover:text-red-500 text-xs px-1.5 py-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              </>
                            )}
                          </div>

                          {editingSlot !== slot.id && (
                            <div className="flex items-center gap-2">
                              <TeamSlotPill
                                team={slot.team1}
                                isEmpty={!slot.team1}
                                isActive={isActive}
                              />
                              <span className="text-gray-400 dark:text-gray-600 font-black text-[10px] shrink-0">
                                VS
                              </span>
                              <TeamSlotPill
                                team={slot.team2}
                                isEmpty={!slot.team2}
                                isActive={isActive}
                              />
                            </div>
                          )}
                        </div>

                        {isActive && (
                          <div className="absolute -top-2.5 left-3">
                            <span className="bg-orange-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full tracking-wide uppercase">
                              Active
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ── Floating save bar ── */}
      {pendingChanges > 0 && (
        <div className="fixed bottom-4 left-0 right-0 flex justify-center px-4 pointer-events-none z-40">
          <div className="pointer-events-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl px-6 py-4 shadow-2xl flex items-center gap-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <span className="font-bold text-gray-900 dark:text-white">
                {pendingChanges}
              </span>{" "}
              change{pendingChanges !== 1 ? "s" : ""} to save
            </div>
            <button
              onClick={saveSchedule}
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-xl text-sm transition active:scale-95"
            >
              {saving ? "Saving…" : "Save to Schedule →"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Team Slot Pill ───────────────────────────────────────────────────────────
function TeamSlotPill({ team, isEmpty, isActive }) {
  if (isEmpty) {
    return (
      <div
        className={`flex-1 h-10 rounded-xl border-2 border-dashed flex items-center justify-center text-xs font-medium transition
        ${
          isActive
            ? "border-orange-400/60 text-orange-400/80 dark:border-orange-500/50 dark:text-orange-500/70"
            : "border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600"
        }`}
      >
        {isActive ? "Select team" : "Empty"}
      </div>
    );
  }
  return (
    <div className="flex-1 h-10 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center gap-2 px-2 overflow-hidden">
      <div
        className="w-6 h-6 rounded-lg shrink-0 overflow-hidden flex items-center justify-center"
        style={{ background: team.logo_url ? "white" : teamColor(team.name) }}
      >
        {team.logo_url ? (
          <img
            src={team.logo_url}
            alt=""
            className="w-full h-full object-contain"
          />
        ) : (
          <span className="text-white text-[8px] font-black">
            {initials(team.name)}
          </span>
        )}
      </div>
      <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">
        {team.name}
      </span>
    </div>
  );
}

// ─── Slot Editor ──────────────────────────────────────────────────────────────
function SlotEditor({ slot, onUpdate, onDone }) {
  const [date, setDate] = useState(slot.date);
  const [time, setTime] = useState(slot.time);
  const [court, setCourt] = useState(slot.court);

  const inputCls =
    "text-xs border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-orange-500 focus:outline-none transition";

  return (
    <div
      className="flex flex-wrap gap-2 items-end w-full"
      onClick={(e) => e.stopPropagation()}
    >
      <div>
        <label className="block text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">
          Date
        </label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={inputCls}
        />
      </div>
      <div>
        <label className="block text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">
          Time
        </label>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className={inputCls}
        />
      </div>
      <div>
        <label className="block text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">
          Court
        </label>
        <select
          value={court}
          onChange={(e) => setCourt(e.target.value)}
          className={inputCls}
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={`Court ${n}`}>
              Court {n}
            </option>
          ))}
        </select>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onUpdate({ date, time, court });
          onDone();
        }}
        className="text-xs bg-orange-600 hover:bg-orange-500 text-white px-3 py-1.5 rounded-lg font-semibold transition"
      >
        Apply
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDone();
        }}
        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-2 py-1.5 transition"
      >
        Cancel
      </button>
    </div>
  );
}
